// The dashboard's conversation pane: transcript + per-subagent activity + composer.
//
// Presentational; `Dashboard.js` stays the single data owner (it already owns
// `fetchProjects`, every handler and every modal). Imports no api module and no
// stylesheet — the `.dash-thread-*` rules live in `Dashboard.css`, which
// `Dashboard.js` imports, matching how `DashboardSidebar` and `DashboardComposer`
// are arranged.
//
// The layout is a column: a head that names the project and offers the canvas, a
// scrolling transcript, then two PINNED strips — the activity list and the
// composer. Pinning the activity list is what makes it useful: the rows mutate
// while you read, so they have to hold still while the transcript moves.

import { Link } from 'react-router-dom';
import AgentParkedBanner from '../components/AgentParkedBanner';
import AgentQuestionCard from '../components/AgentQuestionCard';
import ChatMessageList from '../panels/ChatMessageList';
import DashboardActivity from './DashboardActivity';
import DashboardComposer from './DashboardComposer';
import { serverRunBubble, THINKING } from '../panels/serverRun';
import { summarizeToolCall } from '../panels/chatRounds';
import { firstApproval, firstParked } from '../agent/useAgentRun';
import { MODE_PLAN } from '../panels/chatModes';
import useStickToBottom from '../utils/useStickToBottom';

const RUN_PILL = {
  running: { label: 'Running', className: 'dash-run-pill-running' },
  awaiting_input: { label: 'Needs you', className: 'dash-run-pill-needs-input' },
  paused: { label: 'Paused', className: 'dash-run-pill-paused' },
  done: { label: 'Complete', className: 'dash-run-pill-done' },
  failed: { label: 'Failed', className: 'dash-run-pill-failed' },
  cancelled: { label: 'Cancelled', className: 'dash-run-pill-cancelled' },
};

export default function DashboardThread({
  projectId,
  projectName,
  run,
  messages,
  sending,
  onSend,
  onCancel,
  onPause,
  onResume,
  onOpenTile,
  // Passed straight through to the composer below — this component holds no mode
  // state of its own, for the same reason it holds no message state.
  agentMode,
  onSetAgentMode,
  // `(mode) => Promise` — approve the declared plan and START the build in that
  // permission mode. Handed to the activity panel, because that is where the plan is
  // already rendered; a band here would be a second copy of the same list.
  onApprovePlan,
  // `(document) => Promise` — save the plan the user edited. Also the activity
  // panel's, for the same reason.
  onSavePlan,
  // `(approvalId, decision) => Promise` — decide on one call a manual-mode run is
  // holding, `approve` | `approve_always` | `reject`.
  onApproveCall,
  // `({answers, skipped}) => Promise` — the requirements round, answered as a batch.
  onAnswerQuestions,
  // `(tileId, value) => Promise` — reply to the ONE module a subagent parked with
  // `request_user_input`. Keyed by tileId because several can be parked at once.
  onAnswerParked,
}) {
  const pill = RUN_PILL[run?.status];
  const active = !!run && !['done', 'failed', 'cancelled'].includes(run.status);
  const paused = run?.status === 'paused';
  // The oldest held call, shaped like a tool call so `summarizeToolCall` describes
  // it in the SAME words the transcript uses. Shared with `ChatPanel`'s band, so
  // both surfaces are certainly deciding about the same call.
  const approval = onApproveCall ? firstApproval(run) : null;
  // The oldest module a subagent parked. Same `firstParked` the editor's banner
  // uses, so the two surfaces are certainly answering about the SAME module — a
  // second oldest-first rule here would let them disagree. A cancelled run has an
  // empty `parkedTiles` (the backend settles it in the terminal write), so this
  // goes null on Stop without the client needing a status check of its own.
  const parked = onAnswerParked ? firstParked(run) : null;

  // Follow the newest message unless the user has scrolled up to read back. There is
  // no token stream here — the poll appends a whole message per round — so without
  // this the reply you are waiting for lands below the fold. `sending`/`active` are
  // deps because the thinking indicator below the transcript changes its height.
  // `projectId` re-arms: `Dashboard.js` does not key this component, so a follow
  // broken in one conversation would otherwise be inherited by the next.
  const { scrollerRef, onScroll } = useStickToBottom([messages, sending, active], projectId);

  return (
    <div className="dash-thread">
      <div className="dash-thread-head">
        <span className="dash-thread-name" title={projectName}>
          {projectName || 'Untitled project'}
        </span>
        {pill && <span className={`dash-run-pill ${pill.className}`}>{pill.label}</span>}
        {/* A link, so the canvas can be ctrl/⌘+clicked into a new tab and read
            beside the conversation instead of replacing it. The href is built from
            `projectId` rather than handed down, so there is no callback to keep in
            step with it. */}
        <Link className="applink dash-thread-open" to={`/project/${projectId}`}>
          Open canvas →
        </Link>
        {/* Pause is REVERSIBLE and Stop is not, so they are two controls rather than
            one: holding the pipeline while you think is the common case, abandoning it
            is the rare one. The agent also honours a typed "pause" (its `pause_run`
            tool), but a hold that depends on the model choosing to call a tool is not a
            control — this button posts to the route and cannot be talked out of.
            Titles say what each one leaves running, because "stop" and "pause" are
            near-synonyms in English and only one of them kills a solve. */}
        {active && (paused ? onResume : onPause) && (
          <button
            type="button"
            className="dash-thread-pause"
            onClick={paused ? onResume : onPause}
            title={
              paused
                ? 'Carry on: start whatever is ready'
                : 'Hold the pipeline. Nothing new starts; a solve already running keeps going.'
            }
          >
            {paused ? '▶ Resume' : '❙❙ Pause'}
          </button>
        )}
        {/* A run keeps going with this tab closed, so without a stop the only way
            to end one is to kill its jobs individually from the process monitor. */}
        {active && onCancel && (
          <button
            type="button"
            className="dash-thread-stop"
            onClick={onCancel}
            title="End the run and cancel its jobs. This cannot be undone."
          >
            ✕ Stop
          </button>
        )}
      </div>

      <div className="dash-thread-messages" ref={scrollerRef} onScroll={onScroll}>
        {/* No `isTileKnown`: the dashboard never loads the graph (the server owns
            it during a run), and these tile ids came from the same `/runs` poll
            that produced `parkedTiles`, so they are already server-resolved.
            Passing a predicate that cannot verify would filter every control away
            — see `ChatMessageList.js`. */}
        <ChatMessageList messages={messages} onOpenTile={onOpenTile} />

        {/* A live run has no token stream — there is no SSE on this path — so
            between one persisted message and the next poll NOTHING moves, and the
            agent reads as having stopped when it is mid-turn. This is the only
            thing on screen that says otherwise, which is why it is in the
            transcript rather than only in the panel above: it belongs where the
            reply will appear. Text comes from `serverRunBubble`, so it is
            "Thinking…" before the first round has said anything and the real
            status (with the queue depth) after. */}
        {/* `sending ||` matters: between pressing send and the poll adopting the run,
            `run` is still the PREVIOUS (often terminal) one, so gating on the run
            alone left the composer looking like it had swallowed the message. */}
        {(sending || active) && (
          <div className="dash-thread-thinking" role="status" aria-live="polite">
            <span className="dash-thread-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span className="dash-thread-thinking-text">
              {sending && !active ? THINKING : serverRunBubble(run)}
            </span>
          </div>
        )}
      </div>

      {/* MANUAL mode: a call on the durable run is waiting on a person. Directly
          above the composer — the same position `ChatPanel` uses, and for the same
          reason: it is the thing standing between you and the run continuing, so it
          belongs where you are about to type. Deliberately not a modal; the
          transcript has to stay readable to decide. */}
      {/* The REQUIREMENTS ROUND, above the composer for the reason the approval band
          below is: it is what stands between you and the run continuing. Ordered
          FIRST because it happens before anything is built, so it cannot coexist with
          a decision about a call the build made. The composer stays enabled — saying
          it in prose instead is legitimate. */}
      {onAnswerQuestions && <AgentQuestionCard run={run} onSubmit={onAnswerQuestions} />}

      {/* A MODULE waiting on a person, answerable here rather than only in the
          canvas tile. It sits between the requirements round and the approval band
          because that is its place in time: the round happens before anything is
          built, a park happens mid-build, and a held call is this instant.
          The component is `ProjectPage`'s, reused rather than copied — it already
          owns the rules that are easy to get wrong (with `options` present the
          canned "Done — continue" is NOT offered, because `POST /answer` rejects a
          value outside them) and it imports its own stylesheet, so it carries its
          amber into this chunk instead of depending on `Dashboard.css`. */}
      {parked && <AgentParkedBanner parked={parked} onAnswer={onAnswerParked} />}

      {approval && (
        <div className="dash-approval">
          <div className="dash-approval-question">
            <strong>Approve?</strong> {summarizeToolCall(approval)}
          </div>
          <div className="dash-approval-actions">
            <button
              type="button"
              className="dash-approve-btn"
              onClick={() => onApproveCall(approval.approvalId, 'approve')}
            >
              ✓ Approve
            </button>
            <button
              type="button"
              onClick={() => onApproveCall(approval.approvalId, 'approve_always')}
              title={`Stop asking about ${approval.name} for the rest of this run`}
            >
              Always allow
            </button>
            <button
              type="button"
              className="dash-reject-btn"
              onClick={() => onApproveCall(approval.approvalId, 'reject')}
            >
              ✕ Reject
            </button>
            {/* A NEW TAB, like every other tile link here: going to look at the
                module must not tear down the conversation you are deciding in. */}
            {onOpenTile && approval.tileId && (
              <button type="button" onClick={() => onOpenTile(approval.tileId)}>
                Open module →
              </button>
            )}
          </div>
        </div>
      )}

      <DashboardActivity
        run={run}
        onOpenTile={onOpenTile}
        // Plan mode only, and only once a plan actually exists — the panel gates on
        // both, so a plan cannot be approved before the agent has proposed one.
        planAwaitingApproval={agentMode === MODE_PLAN}
        onApprovePlan={onApprovePlan}
        onSavePlan={onSavePlan}
        // Which of the two approve buttons reads as primary: whatever the composer's
        // chip currently says, so the choice the user already expressed is the one
        // highlighted. It does not restrict the other.
        preferredMode={agentMode}
      />

      <DashboardComposer
        threadActive
        onSend={onSend}
        disabled={sending}
        key={projectId}
        agentMode={agentMode}
        onSetAgentMode={onSetAgentMode}
      />
    </div>
  );
}
